export type ChildOrgsView = 'loading' | 'error' | 'ready';

/**
 * Which of the three child-orgs views the page shows, from the two machine
 * states it can observe.
 *
 * PSTUDIO-4177: `orgRelated.ready.childOrgs.error` is reached whenever
 * `getChildAccounts` throws, and the page's load effect fires only from
 * `childOrgs.idle` — so nothing re-enters `loading` on its own once the machine
 * is parked in `error`. Mapping "not loaded" straight to the loader therefore
 * left a failed load on a spinner that no user action could end; the error view
 * is what carries the retry that sends `manager.childOrgs.loadChildOrgs`.
 *
 * The mapping is total, and an unrecognised combination falls back to the
 * loader rather than to an error the user cannot act on.
 *
 * `loaded` is checked before `failed` for defence, not for a reachable case:
 * the two are sibling sub-states of one region, so a single snapshot can never
 * report both. (During a retry the machine sits in `loading`, i.e. NEITHER is
 * set — that window is `resolveChildOrgsPanels`' job, not this one.) The order
 * only matters if these ever come from separate regions.
 */
export const resolveChildOrgsView = ({
  loaded,
  failed,
}: {
  loaded: boolean;
  failed: boolean;
}): ChildOrgsView => {
  if (loaded) return 'ready';
  if (failed) return 'error';

  return 'loading';
};

/**
 * Which panels the page shows, once the retry-in-flight flag is folded in.
 *
 * PSTUDIO-4177 (WCAG 2.4.3): a retry drives the machine error -> loading, so the
 * naive mapping would swap the error panel for the loader and unmount the button
 * the user had just pressed, dropping focus to `<body>`. Keeping the panel up
 * while the retry is in flight — with the button busy — is what preserves focus,
 * and it is the reason `showLoader` and `showError` are not simply
 * `view === 'loading'` and `view === 'error'`.
 */
export const resolveChildOrgsPanels = ({
  view,
  retrying,
}: {
  view: ChildOrgsView;
  retrying: boolean;
}): { showLoader: boolean; showError: boolean; busyRetry: boolean } => {
  const retryInFlight = retrying && view === 'loading';

  return {
    showLoader: view === 'loading' && !retryInFlight,
    showError: view === 'error' || retryInFlight,
    busyRetry: retryInFlight,
  };
};

/**
 * Where a retry is in its life cycle, which is what separates "loaded because
 * the user retried" from an ordinary load.
 *
 * PSTUDIO-4177. This started as a monotonic `hasRetried` ref latched during
 * render, and review found two defects in that, one of them the opposite of the
 * other. It was never cleared, so after a single retry EVERY later load of the
 * list announced "Child organizations loaded" — and the route is
 * `/organization/:org_id/child-orgs`, so walking into a child org changes only
 * the param and never unmounts the page. And it was latched from a RENDERED
 * FRAME (`busyRetry`), not from the gesture, so a retry whose `loading` frame
 * React batched away announced nothing at all.
 *
 * A phase fixes both: `requested` is written by the click handler, which cannot
 * be missed, and an ordinary `loading` resets it.
 */
export type RetryPhase = 'idle' | 'requested' | 'recovered';

/**
 * The phase after a view change — called from an effect keyed on the VIEW, so a
 * click that has not yet moved the machine out of `error` cannot trip it.
 *
 * LIMITATION, and the one case this does not cover: a retry that fails so fast
 * that `error -> loading -> error` lands in a single commit never changes
 * `view`, so the effect does not run and the phase stays `requested`. The next
 * genuine `loading` then shows the error panel with a busy button instead of the
 * loader, until any later view change clears it. React 18 flushes a discrete
 * event before the promise microtask, so the intermediate frame is normally
 * painted — this is a narrow window, not a guarantee that it cannot happen.
 */
export const nextRetryPhase = ({
  view,
  phase,
}: {
  view: ChildOrgsView;
  phase: RetryPhase;
}): RetryPhase => {
  // Our own retry is in flight; anything else means a fresh load has started
  // and whatever was announced before it no longer applies.
  if (view === 'loading') return phase === 'requested' ? 'requested' : 'idle';

  // Only a load the user ASKED for is worth announcing as a recovery.
  if (view === 'ready') return phase === 'requested' ? 'recovered' : 'idle';

  // `error` announces itself; the phase has nothing left to carry.
  return 'idle';
};

/**
 * What the page's live region should say, if anything.
 *
 * PSTUDIO-4177 (WCAG 4.1.3). The first version announced the error title and
 * nothing else, which left two silences a sighted user does not have: pressing
 * Retry produced no output at all (the button's `aria-busy` is not announced,
 * and the region was being CLEARED at that moment), and a successful retry
 * swapped the panel for the table without a word. Announcing both is what makes
 * the control usable without sight.
 *
 * The `view === 'ready'` guard on `recovered` is load-bearing, not decoration:
 * without it the phase alone would announce a recovery while the next load is
 * still in flight.
 */
export const resolveChildOrgsAnnouncement = ({
  view,
  busyRetry,
  recovered,
}: {
  view: ChildOrgsView;
  busyRetry: boolean;
  recovered: boolean;
}): 'retrying' | 'error' | 'recovered' | 'none' => {
  if (busyRetry) return 'retrying';
  if (view === 'error') return 'error';
  if (view === 'ready' && recovered) return 'recovered';

  return 'none';
};
